import axios from 'axios';

import {
    PrevClientSessionSummary, FullSessionAppointment, SessionActivityName,
    SessionActivity, CreateSessionAppointmentDto, UpdateSessionAppointmentDto,
    VoiceAgentMode,
} from 'interfaces';
import {JoinClientSessionParams} from 'interfaces/client';
import {PaginatedData, PaginationOptions} from 'types';

import {API_URL} from './constants';

const joinAsClient = (data: JoinClientSessionParams) => axios.post(`${API_URL}/session/join/client`, data)
    .then((res) => res && res.data);

const joinAsAgent = async (sessionId: string) => {
    const res = await axios.post(`${API_URL}/session/join/agent`, {sessionId});
    return res.data;
};

const createSession = async (options?: {autoStart: boolean, participantLinkWithToken?: boolean}) => {
    const res = await axios.post(`${API_URL}/session`, {options});
    return res.data;
};

const getSession = async (id: string) => {
    const res = await axios.get(`${API_URL}/session/${id}`);
    return res.data;
};

const getSessionWithState = async (id: string) => {
    const res = await axios.get(`${API_URL}/session/${id}/with-state`);
    return res.data;
};

const getActiveSessionLinks = async () => {
    const res = await axios.post(`${API_URL}/session/active/links`);
    return res.data;
};

const getPreviousSessions = async (id: string): Promise<PrevClientSessionSummary[]> => {
    const res = await axios.get(`${API_URL}/session/${id}/previous`);
    return res.data;
};

const getActivityData = async (id: string, activityName: SessionActivityName): Promise<{id: string, activities: SessionActivity[]}> => {
    let suffix;
    if (activityName === SessionActivityName.SIP) {
        suffix = 'sip';
    } else if (activityName === SessionActivityName.TextChat) {
        suffix = 'text-chat';
    } else if (activityName === SessionActivityName.Avatar) {
        suffix = 'avatar';
    } else {
        throw new Error(`Unexpected activity ${activityName}`);
    }
    const res = await axios.get(`${API_URL}/session/${id}/activity-data/${suffix}`);
    return res.data;
};

const startSession = async (id: string) => {
    const res = await axios.post(`${API_URL}/session/${id}/start`);
    return res.data;
};

const deleteSession = async (sessionId: string, params?: {notify: boolean}) => {
    const res = await axios.delete(`${API_URL}/session/${sessionId}`, {params});
    return res.data;
};

const getAppointments = async (
    body?: {
        participantLinkWithToken: boolean,
        pagination: Pick<PaginationOptions, 'page' | 'pageSize'>,
        myOnly?: boolean
    }): Promise<PaginatedData<FullSessionAppointment>> => {
    const res = await axios.post(`${API_URL}/session/appointments`, body);
    return res.data;
};

const createAppointment = async (data: CreateSessionAppointmentDto, params?: {notify: boolean}): Promise<FullSessionAppointment> => {
    const res = await axios.post(`${API_URL}/session`, data, {params});
    return res.data;
};

const updateAppointment = async (data: UpdateSessionAppointmentDto, params?: {notify: boolean}): Promise<FullSessionAppointment> => {
    const res = await axios.put(`${API_URL}/session/${data.session.id}`, data, {params});
    return res.data;
};

const getSessionAccessToken = async (body: {token: string}) => {
    const res = await axios.post(`${API_URL}/session/session-access-token`, body);
    return res.data;
};

const getOnlineSessions = async () => {
    const res = await axios.post(`${API_URL}/session/online`);
    return res.data;
};

const getMyOnlineSessions = async () => {
    const res = await axios.post(`${API_URL}/session/online/my`);
    return res.data;
};

const getInviteForCalendarFile = async (sessionId: string, participantId: string) => {
    const res = await axios.get(`${API_URL}/session/${sessionId}/invite/${participantId}`);
    return res.data;
};

const switchSipMode = async (sessionId: string, mode: VoiceAgentMode) => {
    const res = await axios.post(`${API_URL}/session/${sessionId}/sip/switch-mode`, {mode});
    return res.data;
};

export const sessionApi = {
    joinAsClient,
    joinAsAgent,
    getSession,
    getSessionWithState,
    getActiveSessionLinks,
    getPreviousSessions,
    startSession,
    createSession,
    deleteSession,
    getAppointments,
    createAppointment,
    updateAppointment,
    getSessionAccessToken,
    getOnlineSessions,
    getMyOnlineSessions,
    getActivityData,
    getInviteForCalendarFile,
    switchSipMode
};
